import {
	Box,
	Flex,
	HStack,
	chakra,
	type HTMLChakraProps,
	type StackProps,
} from '@chakra-ui/react';
import { forwardRef, type MouseEvent, type ReactNode } from 'react';
import { checkboxMenuTriggerMetrics } from '../primitives/CheckboxMenu';
import { Button, type ButtonProps } from '../primitives/Button';
import { Icon } from '../primitives/Icon';
import { Popover, type PopoverProps } from '../primitives/Popover';

/** Same height as LocationSelector / CollapsibleSearch (`checkboxMenuTriggerMetrics`). */
const CONTROL_H = checkboxMenuTriggerMetrics.minH;
/** Same type size as the LocationSelector trigger so toolbar chrome reads as one row. */
const CONTROL_FONT = checkboxMenuTriggerMetrics.fontSize;

/**
 * FilterBar — Brex-style filter chrome (ENG-2298).
 *
 * Order is always left-aligned: active flags, then Add filter, then Clear.
 * Chips are rectangular (`md`), not pills. Color is tokens only.
 * The add-filter panel is `AddFilterMenu` — a portaled popover that stays
 * in the viewport even when the bar sits on the trailing edge of a clipped page.
 */
export function FilterBar({ children, ...rest }: StackProps) {
	return (
		<HStack spacing={3} flexWrap="wrap" align="center" {...rest}>
			{children}
		</HStack>
	);
}

export interface FilterChipProps extends Omit<HTMLChakraProps<'button'>, 'children'> {
	icon?: ReactNode;
	label: ReactNode;
	children?: ReactNode;
	onRemove: () => void;
}

export const FilterChip = forwardRef<HTMLButtonElement, FilterChipProps>(
	({ icon, label, children, onRemove, ...rest }, ref) => (
		<chakra.button
			ref={ref}
			type="button"
			display="inline-flex"
			alignItems="center"
			h={CONTROL_H}
			minH={CONTROL_H}
			p={0}
			overflow="hidden"
			borderWidth="1px"
			borderStyle="solid"
			borderColor="border.default"
			borderRadius="md"
			bg="bg.surface"
			color="fg.default"
			cursor="pointer"
			_hover={{ borderColor: 'border.strong' }}
			{...rest}
		>
			{icon ? (
				<>
					<Flex align="center" justify="center" px={2} h="100%" color="fg.default" flexShrink={0}>
						{icon}
					</Flex>
					<Box w="1px" alignSelf="stretch" bg="border.subtle" flexShrink={0} />
				</>
			) : null}
			<Flex align="center" px={2.5} h="100%" minW={0}>
				<Box
					as="span"
					fontSize={CONTROL_FONT}
					fontWeight="medium"
					color="fg.default"
					lineHeight="1.2"
					whiteSpace="nowrap"
				>
					{children ?? label}
				</Box>
			</Flex>
			<Box w="1px" alignSelf="stretch" bg="border.subtle" flexShrink={0} />
			<Flex
				as="span"
				role="button"
				aria-label="Remove filter"
				align="center"
				justify="center"
				px={1.5}
				h="100%"
				flexShrink={0}
				onClick={(event: MouseEvent) => {
					event.preventDefault();
					event.stopPropagation();
					onRemove();
				}}
			>
				<Flex
					align="center"
					justify="center"
					w="1.125rem"
					h="1.125rem"
					borderRadius="full"
					bg="bg.muted"
					color="fg.muted"
					_hover={{ bg: 'border.default', color: 'fg.default' }}
				>
					<Icon name="x" size="xs" />
				</Flex>
			</Flex>
		</chakra.button>
	),
);
FilterChip.displayName = 'FilterChip';

export interface AddFilterButtonProps extends HTMLChakraProps<'button'> {
	children?: ReactNode;
}

export const AddFilterButton = forwardRef<HTMLButtonElement, AddFilterButtonProps>(
	({ children = 'Add filter', ...rest }, ref) => (
		<chakra.button
			ref={ref}
			type="button"
			display="inline-flex"
			alignItems="center"
			justifyContent="center"
			gap={1.5}
			h={CONTROL_H}
			minH={CONTROL_H}
			px={2.5}
			borderWidth="1px"
			borderStyle="dashed"
			borderColor="border.default"
			borderRadius="md"
			bg="transparent"
			color="fg.default"
			fontSize={CONTROL_FONT}
			fontWeight="medium"
			lineHeight="short"
			whiteSpace="nowrap"
			cursor="pointer"
			_hover={{ bg: 'bg.subtle', borderColor: 'border.strong' }}
			{...rest}
		>
			<Icon name="filter" size="sm" />
			{children}
		</chakra.button>
	),
);
AddFilterButton.displayName = 'AddFilterButton';

const VIEWPORT_PADDING = 8;

export interface AddFilterMenuProps {
	isOpen: boolean;
	onToggle: () => void;
	children: ReactNode;
	width?: PopoverProps['width'];
	contentProps?: PopoverProps['contentProps'];
}

/**
 * Add-filter trigger + menu. Positions with Popper (`bottom-start`, flip /
 * preventOverflow, fixed strategy) so parent alignment does not matter.
 */
export function AddFilterMenu({
	isOpen,
	onToggle,
	children,
	width = '220px',
	contentProps,
}: AddFilterMenuProps) {
	return (
		<Popover
			isOpen={isOpen}
			placement="bottom-start"
			gutter={4}
			closeOnBlur={false}
			width={width}
			contentProps={contentProps}
			modifiers={[
				{
					name: 'preventOverflow',
					options: { padding: VIEWPORT_PADDING, altAxis: true, tether: false },
				},
			]}
			trigger={<AddFilterButton aria-label="Add filter" onClick={onToggle} />}
		>
			{children}
		</Popover>
	);
}

export const ClearFiltersButton = forwardRef<HTMLButtonElement, ButtonProps>(
	({ children = 'Clear filters', ...rest }, ref) => (
		<Button
			ref={ref}
			variant="ghost"
			colorScheme="red"
			size="toolbar"
			h={CONTROL_H}
			px={2}
			leftIcon={<Icon name="x" size="xs" />}
			fontSize={CONTROL_FONT}
			fontWeight="medium"
			{...rest}
		>
			{children}
		</Button>
	),
);
ClearFiltersButton.displayName = 'ClearFiltersButton';
